import type { LibraryItem, LibrarySearchResult } from "./types";

export const emptyLibraryDraft = (): LibrarySearchResult => ({
  sourceProvider: "manual",
  sourceType: "book",
  sourceId: null,
  googleBooksId: null,
  openLibraryId: null,
  title: "",
  subtitle: null,
  displayTitle: null,
  authorText: null,
  authors: [],
  publisher: null,
  publishedDate: null,
  publishedYear: null,
  languageCode: null,
  isbn10: null,
  isbn13: null,
  edition: null,
  volume: null,
  series: null,
  pageCount: null,
  coverUrl: null,
  coverCandidates: [],
  description: null,
  doi: null,
  url: null,
  containerTitle: null,
  accessedDate: null,
  openAccessUrl: null,
  metadata: {},
  status: "to_read",
  isOwned: false,
  bookBinding: null,
  isFirstEdition: false,
  isSigned: false,
  isNumbered: false,
  numberedIndex: null,
  numberedTotal: null,
  purchaseCostCents: null,
  replacementCostCents: null,
  citationStyleOverride: null,
  completeness: { hasIsbn: false, hasCover: false, hasPublisher: false, hasPageCount: false, hasDescription: false },
});

export const normalizeLibraryDraft = (draft: LibrarySearchResult & { notes?: string | null }) => {
  const normalizedPublishedDate = draft.publishedDate?.trim() || null;
  const normalizedAuthorText = draft.authorText?.trim() || null;
  const normalizedAuthors = (normalizedAuthorText
    ? normalizedAuthorText.split(",")
    : draft.authors
  )
    .map((value) => value.trim())
    .filter(Boolean);
  const publishedYearMatch = normalizedPublishedDate?.match(/^(\d{4})/);

  return {
    ...draft,
    title: draft.title.trim(),
    subtitle: draft.subtitle?.trim() || null,
    displayTitle: draft.displayTitle?.trim() || null,
    authorText: normalizedAuthorText,
    authors: normalizedAuthors,
    publisher: draft.publisher?.trim() || null,
    publishedDate: normalizedPublishedDate,
    publishedYear: publishedYearMatch ? Number.parseInt(publishedYearMatch[1], 10) : (draft.publishedYear ?? null),
    languageCode: draft.languageCode?.trim() || null,
    isbn10: draft.isbn10?.trim() || null,
    isbn13: draft.isbn13?.trim() || null,
    edition: draft.edition?.trim() || null,
    volume: draft.volume?.trim() || null,
    series: draft.series?.trim() || null,
    pageCount: typeof draft.pageCount === "number" && Number.isFinite(draft.pageCount) ? draft.pageCount : null,
    description: draft.description?.trim() || null,
    notes: draft.notes?.trim() || null,
    status: draft.status,
  };
};

// Inverse of normalizeLibraryDraft / saveLibraryItem payload — used when
// re-opening AddSourceInlineDialog in edit mode for an existing library item.
export const libraryItemToDraft = (item: LibraryItem): LibrarySearchResult & { notes: string | null } => ({
  sourceProvider: (item.source_provider as LibrarySearchResult["sourceProvider"]) ?? "manual",
  sourceType: item.source_type,
  sourceId: item.source_id ?? null,
  googleBooksId: item.google_books_id ?? null,
  openLibraryId: item.open_library_id ?? null,
  title: item.title ?? "",
  subtitle: item.subtitle ?? null,
  displayTitle: item.display_title ?? null,
  authorText: item.author_text ?? null,
  authors: Array.isArray(item.authors) ? (item.authors as unknown as string[]) : [],
  publisher: item.publisher ?? null,
  publishedDate: item.published_date ?? null,
  publishedYear: item.published_year ?? null,
  languageCode: item.language_code ?? null,
  isbn10: item.isbn_10 ?? null,
  isbn13: item.isbn_13 ?? null,
  edition: item.edition ?? null,
  volume: item.volume ?? null,
  series: item.series ?? null,
  pageCount: item.page_count ?? null,
  coverUrl: item.cover_url ?? null,
  coverCandidates: item.cover_url ? [item.cover_url] : [],
  description: item.description ?? null,
  doi: item.doi ?? null,
  url: item.url ?? null,
  containerTitle: item.container_title ?? null,
  accessedDate: item.accessed_date ?? null,
  openAccessUrl: (item as { open_access_url?: string | null }).open_access_url ?? null,
  metadata: (item.metadata as Record<string, unknown> | null) ?? {},
  status: item.status,
  isOwned: item.is_owned ?? false,
  bookBinding: (item.book_binding as "paperback" | "hardback" | null) ?? null,
  isFirstEdition: item.is_first_edition ?? false,
  isSigned: item.is_signed ?? false,
  isNumbered: item.is_numbered ?? false,
  numberedIndex: item.numbered_index ?? null,
  numberedTotal: item.numbered_total ?? null,
  purchaseCostCents: (item as { purchase_cost_cents?: number | null }).purchase_cost_cents ?? null,
  replacementCostCents: (item as { replacement_cost_cents?: number | null }).replacement_cost_cents ?? null,
  citationStyleOverride:
    (item.citation_style_override as string | null | undefined) ?? null,
  notes: item.notes ?? null,
  completeness: {
    hasIsbn: !!(item.isbn_13 || item.isbn_10),
    hasCover: !!item.cover_url,
    hasPublisher: !!item.publisher,
    hasPageCount: !!item.page_count,
    hasDescription: !!item.description,
  },
});

export const deriveLibraryTitle = (item: Pick<LibraryItem, "title" | "display_title" | "subtitle">) => {
  const base = item.display_title?.trim() || item.title.trim();
  return item.subtitle ? `${base}: ${item.subtitle}` : base;
};
